// Las fechas son el dato central de un evento, no un campo más: el difusor
// decide en función de cuándo es, y su plazo para responder sale de ahí.
// Estas ayudas se usan tanto en el alta como en los listados.

const MS_DIA = 86400000;

// Hoy a las 00:00. Un evento que es HOY todavía sirve; uno de ayer, no.
export function hoy() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

// Un instante. Puede llegar como Date, como texto ISO o como epoch en ms
// —número o string de solo dígitos, que es como se guarda `envios.fecha`—.
// Sin el caso de los dígitos, new Date("1758300000000") da Invalid Date.
function aInstante(valor) {
  if (valor === null || valor === undefined || valor === "") return null;
  const d =
    valor instanceof Date
      ? valor
      : /^\d+$/.test(String(valor))
      ? new Date(Number(valor))
      : new Date(valor);
  return Number.isNaN(d.getTime()) ? null : d;
}

// La fecha de un evento es un día de calendario, no un instante. El backend
// la guarda como DATETIME y la devuelve en UTC ("2099-01-15T00:00:00.000Z"),
// así que pasarla tal cual a new Date() le muestra el día anterior a todo el
// que esté al oeste de Greenwich. Se toma la parte de la fecha y se arma el
// Date con sus números, que ya quedan en hora local.
function aFechaLocal(valor) {
  const partes = String(valor ?? "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (partes)
    return new Date(Number(partes[1]), Number(partes[2]) - 1, Number(partes[3]));
  return aInstante(valor);
}

// "2026-12-01" para los <input type="date">, en hora local: usar toISOString
// correría la fecha un día para quien esté al oeste de Greenwich.
export function aInputDate(valor) {
  const d = aFechaLocal(valor);
  if (!d) return "";
  const mes = String(d.getMonth() + 1).padStart(2, "0");
  const dia = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mes}-${dia}`;
}

export function formatearFecha(valor) {
  const d = aFechaLocal(valor);
  if (!d) return "";
  return d.toLocaleDateString("es", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// Días que faltan. Negativo si ya pasó.
export function diasHasta(valor) {
  const d = aFechaLocal(valor);
  if (!d) return null;
  d.setHours(0, 0, 0, 0);
  return Math.round((d - hoy()) / MS_DIA);
}

// Texto de cuenta regresiva, listo para mostrar.
export function cuentaRegresiva(valor) {
  const dias = diasHasta(valor);
  if (dias === null) return "";
  if (dias < 0) return `hace ${Math.abs(dias)} día${Math.abs(dias) === 1 ? "" : "s"}`;
  if (dias === 0) return "hoy";
  if (dias === 1) return "mañana";
  return `en ${dias} días`;
}

// Sugerencia de cuándo publicar la nota: una semana antes del evento, o
// cuanto antes si falta menos. Es solo el valor precargado; lo decide el
// artista, que sabe si hay embargo de prensa, preventa o anuncio coordinado.
export const DIAS_RECOMENDADOS_ANTES = 7;

export function fechaRecomendadaSugerida(fechaEvento) {
  const d = aFechaLocal(fechaEvento);
  if (!d) return "";
  const sugerida = new Date(d.getTime() - DIAS_RECOMENDADOS_ANTES * MS_DIA);
  return aInputDate(sugerida < hoy() ? hoy() : sugerida);
}

// Cuánto le queda al difusor para responder un envío, en horas. El backend
// manda el plazo ya calculado (`horas_limite`), que para un evento es la
// mitad y está topado por la fecha del show.
export function horasRestantes(fechaEnvio, horasLimite) {
  if (horasLimite == null) return null;
  // Acá sí importa el instante, no el día: `envios.fecha` es el epoch en ms
  // guardado como texto.
  const desde = aInstante(fechaEnvio);
  if (!desde) return null;
  const transcurridas = (Date.now() - desde.getTime()) / 3600000;
  return Math.max(0, Math.round(horasLimite - transcurridas));
}

// El mismo dato, en palabras.
export function plazoEnPalabras(fechaEnvio, horasLimite) {
  const horas = horasRestantes(fechaEnvio, horasLimite);
  if (horas === null) return "";
  if (horas <= 0) return "el plazo venció";
  if (horas < 24) return `quedan ${horas} h`;
  const dias = Math.floor(horas / 24);
  return `${dias} día${dias === 1 ? "" : "s"}`;
}
